import { useEffect, useRef } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const GeoDataProcessor = ({ kmlUrl, isExploitation, exploitationId }) => {
  const maps = useMapsLibrary("maps");
  const map = useMap();
  const textOverlaysRef = useRef([]);
  const navigate = useNavigate();
  const userRole = useSelector((state) => state.user.userRole.name);
  const roleLink = () => {
    if (userRole === 'client') {
      return "/client";
    } else if (userRole === 'admin' || userRole === 'superadmin') {
      return "/admin";
    } else if (userRole === 'domain_manager') {
      return "/domain-manager";
    } else if (userRole === 'exploitation_manager') {
      return "/exploitation-manager";
    }
  };

  useEffect(() => {
    if (maps && map) {
      const dataLayer = new maps.Data({ map });
      const bounds = new window.google.maps.LatLngBounds();

      fetch(kmlUrl)
        .then((response) => response.text())
        .then((kmlText) => {
          // Parse KML data
          const kmlDocument = new DOMParser().parseFromString(
            kmlText,
            "text/xml"
          );
          const geoJsonData = kmlToGeoJson(kmlDocument);
          dataLayer.addGeoJson(geoJsonData);

          dataLayer.addListener("click", (event) => {
            navigate(`${roleLink()}/exploitation/${exploitationId}/info/irrigation`);
          

            // alert("You clicked on a ", event.feature.getProperty("name"))
          });

          // Set styles and add text labels to polygons
          dataLayer.setStyle((feature) => {
            const color = feature.getProperty("fillColor") || "#FF0000";
            return {
              fillColor: color,
              strokeColor: color,
              strokeWeight: 3,
              fillOpacity: 0.14,
            };
          });

          dataLayer.forEach((feature) => {
            const geometry = feature.getGeometry();
            if (geometry.getType() === "Polygon") {
              geometry.getArray().forEach((path) => {
                path.getArray().forEach((latlng) => {
                  bounds.extend(latlng);
                });
              });

              if (!isExploitation) {
                const center = bounds.getCenter();
                const textOverlay = new window.google.maps.OverlayView();
                textOverlay.onAdd = function () {
                  const div = document.createElement("div");
                  div.style.position = "absolute";
                  div.style.background = "white";
                  div.style.padding = "5px 5px";
                  div.style.border = "1px solid black";
                  div.style.borderRadius = "20px";
                  div.style.outline = "none";
                  div.style.zIndex = 1000;
                  div.style.fontWeight = "900";
                  div.innerHTML = feature.getProperty("name") || "Polygon";

                  this.div = div;

                  const panes = this.getPanes();
                  panes.overlayLayer.appendChild(div);
                };

                textOverlay.draw = function () {
                  const overlayProjection = this.getProjection();
                  const position =
                    overlayProjection.fromLatLngToDivPixel(center);

                  const div = this.div;
                  div.style.left = position.x - div.offsetWidth / 2 + "px";
                  div.style.top = position.y + "px";
                };

                textOverlay.onRemove = function () {
                  this.div.parentNode.removeChild(this.div);
                  this.div = null;
                };

                textOverlay.setMap(map);
                textOverlaysRef.current.push(textOverlay);
              }
            }
          });

          // Fit map to bounds
          if (isExploitation) map.fitBounds(bounds);
        })
        .catch((error) => console.error("Error loading KML file:", error));

      return () => {
        dataLayer.setMap(null);

        // Clear all text overlays
        textOverlaysRef.current.forEach((overlay) => {
          overlay.setMap(null);
        });
        textOverlaysRef.current = []; // Reset the array
      };
    }
  }, [map, maps, kmlUrl, isExploitation]);

  return <></>;
};

// Helper function to convert KML to GeoJSON and extract styles
const kmlToGeoJson = (kml) => {
  const geoJson = {
    type: "FeatureCollection",
    features: [],
  };

  const placemarks = kml.getElementsByTagName("Placemark");
  for (let i = 0; i < placemarks.length; i++) {
    const placemark = placemarks[i];
    const name = placemark.getElementsByTagName("name")[0].textContent;
    const polygon = placemark.getElementsByTagName("Polygon")[0];

    let fillColor = "#FF0000"; // Default color if not specified

    const style = kml.getElementsByTagName("Style")[0];
    if (style) {
      const lineStyle = style.getElementsByTagName("LineStyle")[0];
      if (lineStyle) {
        const colorElement = lineStyle.getElementsByTagName("color")[0];
        if (colorElement) {
          const kmlColor = colorElement.textContent.trim();
          fillColor = kmlColorToHex(kmlColor);
        }
      }
    }

    if (polygon) {
      const coordinates = polygon
        .getElementsByTagName("coordinates")[0]
        .textContent.trim();
      const coordArray = coordinates.split(/\s+/).map((coord) => {
        const [lng, lat] = coord.split(",").map(Number);
        return [lng, lat];
      });
      const feature = {
        type: "Feature",
        properties: { name, fillColor },
        geometry: {
          type: "Polygon",
          coordinates: [coordArray],
        },
      };

      geoJson.features.push(feature);
    }
  }

  return geoJson;
};

// Helper function to convert KML color format (aabbggrr) to hex format (#rrggbb)
const kmlColorToHex = (kmlColor) => {
  const a = kmlColor.substring(0, 2);
  const b = kmlColor.substring(2, 4);
  const g = kmlColor.substring(4, 6);
  const r = kmlColor.substring(6, 8);

  return `#${r}${g}${b}`;
};

export default GeoDataProcessor;
